import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, FileText, PlayCircle, Users, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { AgeChip } from "@/lib/patient";
import { Action, EmptyState, Panel, PanelHeader, SearchField, StatusBadge } from "./ui";
import { formatDay } from "./MeetingGrid";

// Tela de condução da reunião: detalhe, lista de presença opcional e conclusão.
export default function MeetingGridRoom() {
  const { occurrenceId } = useParams();
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: payload } = await api.get(`/portal/meeting-grid/occurrences/${occurrenceId}`);
      setData(payload);
      setNotes(payload.notes || "");
      setSelected(new Set(payload.attendance.map((a) => a.patient_id)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occurrenceId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!data || data === false) return;
    const timer = setTimeout(async () => {
      try {
        const { data: list } = await api.get(`/portal/meeting-grid/occurrences/${occurrenceId}/eligible-patients`, {
          params: { q: query || undefined },
        });
        setPatients(list);
      } catch {
        setPatients([]);
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, data?.id, occurrenceId]);

  const act = async (fn, message) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      toast.success(message);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (data === false) return <EmptyState testId="grid-room-error" title="Reunião não disponível" description="Você não possui acesso a esta reunião." />;
  if (!data) return null;

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  return (
    <div data-testid="grid-room-page" className="space-y-6">
      <Panel testId="grid-room-header" className="space-y-3">
        {data.status === "in_progress" ? (
          <p data-testid="grid-room-live" className="inline-flex items-center gap-2 rounded-full bg-sage-600 px-4 py-1.5 text-[13px] font-semibold uppercase tracking-wide text-white">
            Reunião em andamento
          </p>
        ) : null}
        <PanelHeader
          label={`${data.ward_label} · ${formatDay(data.date)}`}
          title={data.topic_name}
          hint={`Previsto ${data.start_time}–${data.end_time} (${data.duration_minutes} min) · Responsável: ${data.responsible_name || "—"}`}
          actions={
            <Link to="/portal/grade-reunioes" data-testid="grid-room-back" className="text-[14px] font-semibold text-sage-700 hover:underline">
              Voltar para a grade
            </Link>
          }
        />
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge tone={data.status === "done" ? "active" : data.status === "cancelled" ? "alert" : "pending"}>{data.status_label}</StatusBadge>
          {data.pending_confirmation ? <StatusBadge tone="pending">Pendente de confirmação</StatusBadge> : null}
          {data.attendance_registered ? <span className="text-[13.5px] text-ink-600">Presentes: {data.attendance_count} pacientes</span> : <span className="text-[13.5px] text-ink-500">Sem lista de presença registrada</span>}
        </div>
        <div className="grid gap-1.5 text-[13.5px] text-ink-600 sm:grid-cols-2">
          <p>Início real: {data.actual_start ? new Date(data.actual_start).toLocaleString("pt-BR") : "—"}</p>
          <p>Término real: {data.actual_end ? new Date(data.actual_end).toLocaleString("pt-BR") : "—"}</p>
          {data.started_by_name ? <p>Iniciada por: {data.started_by_name}</p> : null}
          {data.finished_by_name ? <p>Finalizada por: {data.finished_by_name}</p> : null}
          {data.summary ? <p className="sm:col-span-2">Resumo: {data.summary}</p> : null}
          {data.cancel_reason ? <p className="sm:col-span-2">Motivo do cancelamento: {data.cancel_reason}</p> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {data.topic?.material ? (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/v1/portal/meeting-grid/topics/${data.topic.id}/material`}
              target="_blank"
              rel="noreferrer"
              data-testid="grid-room-material"
              className="inline-flex min-h-[40px] items-center gap-2 rounded-full bg-sand px-4 text-[14px] font-semibold text-ink-700"
            >
              <FileText size={16} aria-hidden="true" /> Abrir apresentação
            </a>
          ) : null}
          {data.can_conduct && data.status === "scheduled" ? (
            <>
              <Action icon={PlayCircle} data-testid="grid-room-start" onClick={() => act(() => api.post(`/portal/meeting-grid/occurrences/${occurrenceId}/start`), "Reunião iniciada.")}>
                Iniciar reunião
              </Action>
              <Action
                variant="secondary"
                icon={CheckCircle2}
                data-testid="grid-room-complete"
                onClick={() =>
                  act(
                    () =>
                      api.post(`/portal/meeting-grid/occurrences/${occurrenceId}/finish`, {
                        notes,
                        patient_ids: selected.size ? Array.from(selected) : undefined,
                      }),
                    "Reunião marcada como realizada."
                  )
                }
              >
                Marcar como realizada
              </Action>
            </>
          ) : null}
          {data.can_conduct && data.status === "in_progress" ? (
            <Action
              icon={CheckCircle2}
              data-testid="grid-room-finish"
              onClick={() => act(() => api.post(`/portal/meeting-grid/occurrences/${occurrenceId}/finish`, { notes, patient_ids: Array.from(selected) }), "Reunião finalizada.")}
            >
              Finalizar reunião
            </Action>
          ) : null}
          {data.can_manage && ["scheduled", "in_progress"].includes(data.status) ? (
            <Action
              variant="ghost"
              icon={XCircle}
              data-testid="grid-room-cancel"
              onClick={() => {
                const reason = window.prompt("Motivo do cancelamento:");
                if (reason && reason.trim())
                  act(() => api.post(`/portal/meeting-grid/occurrences/${occurrenceId}/cancel`, { reason }), "Reunião cancelada.");
              }}
            >
              Cancelar reunião
            </Action>
          ) : null}
        </div>
      </Panel>

      {data.can_conduct && data.status !== "cancelled" ? (
        <Panel testId="grid-room-attendance" className="space-y-4">
          <PanelHeader
            label="Opcional"
            title="Lista de presença"
            hint={`Somente pacientes ativos da ${data.ward_label}. A lista não é obrigatória para concluir a reunião.`}
            actions={
              <Action
                size="sm"
                icon={Users}
                data-testid="grid-room-save-attendance"
                onClick={() => act(() => api.put(`/portal/meeting-grid/occurrences/${occurrenceId}/attendance`, { patient_ids: Array.from(selected) }), "Lista de presença salva.")}
              >
                Salvar lista ({selected.size})
              </Action>
            }
          />
          <SearchField testId="grid-room-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar paciente pelo nome" />
          <div className="max-h-[420px] space-y-2 overflow-y-auto">
            {patients.length === 0 ? (
              <p className="text-[14px] text-ink-500">Nenhum paciente ativo encontrado nesta ala.</p>
            ) : (
              patients.map((p) => (
                <label
                  key={p.id}
                  data-testid={`grid-attendance-${p.id}`}
                  className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-white p-3"
                >
                  <input
                    type="checkbox"
                    data-testid={`grid-attendance-checkbox-${p.id}`}
                    checked={selected.has(p.id)}
                    onChange={() => toggle(p.id)}
                    className="h-5 w-5 rounded border-border accent-sage-600"
                  />
                  {p.photo_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${p.photo_url}`} alt="" className="h-9 w-9 rounded-full object-cover" />
                  ) : null}
                  <span className="min-w-0">
                    <span className="block text-[14.5px] font-semibold text-ink-900">
                      {p.full_name} <AgeChip patient={p} />
                    </span>
                    <span className="block text-[13px] text-ink-500">Conselheiro: {p.counselor_name}</span>
                  </span>
                </label>
              ))
            )}
          </div>
          <label className="block">
            <span className="text-[13.5px] font-semibold text-ink-700">Observações</span>
            <textarea
              rows={3}
              data-testid="grid-room-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-border p-3 text-[14.5px]"
            />
          </label>
        </Panel>
      ) : null}

      {data.status === "done" && data.attendance.length > 0 ? (
        <Panel testId="grid-room-present-list" className="space-y-2">
          <PanelHeader label="Registro" title={`Presentes: ${data.attendance_count} pacientes`} />
          {data.attendance.map((a) => (
            <p key={a.patient_id} className="text-[14px] text-ink-700">
              {a.patient_name}
            </p>
          ))}
        </Panel>
      ) : null}
    </div>
  );
}
